import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Clock,
  Save,
  AlertCircle,
  CheckCircle,
  Timer,
  Info,
  ChevronRight,
  ShieldCheck,
  Zap,
  Bell
} from 'lucide-react';
import { useTheme } from '../guardarToken/themeContext';
import BotonAcceso from '../elementos/BotonAcceso';

const apiUrlConfiguracion = process.env.REACT_APP_API_CONFIGURACION_SEMANAL || '/api/menus/configuracion-semanal/';

const diasSemana = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' }
];

const ConfiguracionSemanal = () => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [config, setConfig] = useState({
    dia_limite_pedido: 4,        // Jueves
    hora_limite_pedido: 14,       // 14:00
    minuto_limite_pedido: 0,
    dia_limite_modificacion: 5,   // Viernes
    hora_limite_modificacion: 18, // 18:00
    minuto_limite_modificacion: 0,
    semana_referencia: 'anterior'
  });

  useEffect(() => {
    fetchConfiguracion();
  }, []);

  const fetchConfiguracion = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(apiUrlConfiguracion, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const payload = Array.isArray(response.data) ? response.data[0] : response.data;
      if (payload) {
        setConfig(prev => ({ ...prev, ...payload }));
      }
      setError(null);
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      setError('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(apiUrlConfiguracion, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Configuración guardada correctamente');
      setTimeout(() => setSuccess(null), 5000);
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      setError(error.response?.data?.message || 'Error al guardar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: parseInt(value) || value }));
  };

  const calcularFechaEjemplo = () => {
    const hoy = new Date();
    const diasHastaLunes = (8 - hoy.getDay()) % 7 || 7;
    const proximoLunes = new Date(hoy);
    proximoLunes.setDate(hoy.getDate() + diasHastaLunes);

    let fechaPedido = new Date(proximoLunes);
    if (config.semana_referencia === 'anterior') {
      fechaPedido.setDate(fechaPedido.getDate() - 7);
    }
    const diasDesdeLunes = (config.dia_limite_pedido + 7 - 1) % 7;
    fechaPedido.setDate(fechaPedido.getDate() + diasDesdeLunes);
    fechaPedido.setHours(config.hora_limite_pedido, config.minuto_limite_pedido, 0, 0);

    let fechaModif = new Date(proximoLunes);
    if (config.semana_referencia === 'anterior') {
      fechaModif.setDate(fechaModif.getDate() - 7);
    }
    const diasDesdeLunesModif = (config.dia_limite_modificacion + 7 - 1) % 7;
    fechaModif.setDate(fechaModif.getDate() + diasDesdeLunesModif);
    fechaModif.setHours(config.hora_limite_modificacion, config.minuto_limite_modificacion, 0, 0);

    return {
      semana: proximoLunes.toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' }),
      pedido: fechaPedido.toLocaleString('es-AR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      }),
      modificacion: fechaModif.toLocaleString('es-AR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  const ejemplo = calcularFechaEjemplo();

  return (
    <div className={`min-h-screen p-3 lg:p-4 font-sans transition-colors duration-300 ${isDarkMode ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-800"}`}>

      {/* HEADER PREMIUM */}
      <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-2xl shadow-sm p-5 mb-6 flex flex-col md:flex-row items-center justify-between border ring-1 ring-black/5 animate-in slide-in-from-top duration-700`}>
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-3 rounded-xl shadow-lg shadow-indigo-900/20">
            <Timer className="text-white w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight leading-none mb-1">Configuración de Plazos</h1>
            <p className={`text-[11px] ${isDarkMode ? "text-slate-400" : "text-slate-500"} font-black uppercase tracking-wider`}>Gestiona fechas límite para pedidos y cambios</p>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-4 md:mt-0">
          <BotonAcceso
            variant="accent"
            onClick={handleSubmit}
            disabled={loading}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-black text-[11px] uppercase tracking-widest px-6 py-2.5 rounded-xl shadow-lg shadow-indigo-950/20 flex items-center gap-2 transform hover:scale-[1.02] transition-all disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {loading ? 'Guardando...' : 'Guardar Cambios'}
          </BotonAcceso>
        </div>
      </div>

      <div className="max-w-5xl mx-auto space-y-6">

        {/* MESSAGES */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-500 rounded-xl px-5 py-3 text-xs font-black uppercase tracking-wider flex items-center gap-3 animate-in zoom-in duration-300">
            <AlertCircle className="w-5 h-5" /> {error}
          </div>
        )}
        {success && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 rounded-xl px-5 py-3 text-xs font-black uppercase tracking-wider flex items-center gap-3 animate-in zoom-in duration-300">
            <CheckCircle className="w-5 h-5" /> {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* FORM AREA */}
          <div className="lg:col-span-2 space-y-6 animate-in slide-in-from-left duration-1000">

            {/* SEMANA DE REFERENCIA */}
            <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-3xl p-6 border shadow-sm`}>
              <div className="flex items-center gap-3 mb-6">
                <Zap className="w-5 h-5 text-amber-500" />
                <h3 className="text-sm font-black uppercase tracking-widest">Semana de Referencia</h3>
              </div>
              <div className="space-y-4">
                <select
                  value={config.semana_referencia}
                  onChange={(e) => handleChange('semana_referencia', e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest focus:outline-none transition-all ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-200 text-slate-800"
                    } border ring-2 ring-indigo-500/0 focus:ring-indigo-500/20`}
                >
                  <option value="anterior">Semana anterior al pedido (Recomendado)</option>
                  <option value="misma">Misma semana del pedido</option>
                </select>
                <p className="text-[10px] font-bold text-slate-500 leading-relaxed italic">
                  Determina en relación a qué semana se calculan los días y horarios límite.
                </p>
              </div>
            </div>

            {/* LIMITS SECTION */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* PEDIDOS LIMIT */}
              <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-3xl p-6 border shadow-sm`}>
                <div className="flex items-center gap-3 mb-6">
                  <Bell className="w-5 h-5 text-emerald-500" />
                  <h3 className="text-sm font-black uppercase tracking-widest leading-none">Cierre de Pedidos</h3>
                </div>

                <div className="space-y-5">
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1">Día de Cierre</label>
                    <select
                      value={config.dia_limite_pedido}
                      onChange={(e) => handleChange('dia_limite_pedido', e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest focus:outline-none ${isDarkMode ? "bg-slate-950 border-slate-800" : "bg-slate-50 border-slate-200"
                        } border`}
                    >
                      {diasSemana.map(dia => (
                        <option key={dia.value} value={dia.value}>{dia.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1">Hora (0-23)</label>
                      <input
                        type="number"
                        min="0" max="23"
                        value={config.hora_limite_pedido}
                        onChange={(e) => handleChange('hora_limite_pedido', e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl text-xs font-bold focus:outline-none transition-all ${isDarkMode ? "bg-slate-950 border-slate-800" : "bg-slate-50 border-slate-200"
                          } border`}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1">Minutos</label>
                      <input
                        type="number"
                        min="0" max="59"
                        value={config.minuto_limite_pedido}
                        onChange={(e) => handleChange('minuto_limite_pedido', e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl text-xs font-bold focus:outline-none transition-all ${isDarkMode ? "bg-slate-950 border-slate-800" : "bg-slate-50 border-slate-200"
                          } border`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* MODIFICATION LIMIT */}
              <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-3xl p-6 border shadow-sm`}>
                <div className="flex items-center gap-3 mb-6">
                  <Clock className="w-5 h-5 text-orange-500" />
                  <h3 className="text-sm font-black uppercase tracking-widest leading-none">Modificaciones</h3>
                </div>

                <div className="space-y-5">
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1">Último Cambio</label>
                    <select
                      value={config.dia_limite_modificacion}
                      onChange={(e) => handleChange('dia_limite_modificacion', e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest focus:outline-none ${isDarkMode ? "bg-slate-950 border-slate-800" : "bg-slate-50 border-slate-200"
                        } border`}
                    >
                      {diasSemana.map(dia => (
                        <option key={dia.value} value={dia.value}>{dia.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1">Hora (0-23)</label>
                      <input
                        type="number"
                        min="0" max="23"
                        value={config.hora_limite_modificacion}
                        onChange={(e) => handleChange('hora_limite_modificacion', e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl text-xs font-bold focus:outline-none transition-all ${isDarkMode ? "bg-slate-950 border-slate-800" : "bg-slate-50 border-slate-200"
                          } border`}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1">Minutos</label>
                      <input
                        type="number"
                        min="0" max="59"
                        value={config.minuto_limite_modificacion}
                        onChange={(e) => handleChange('minuto_limite_modificacion', e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl text-xs font-bold focus:outline-none transition-all ${isDarkMode ? "bg-slate-950 border-slate-800" : "bg-slate-50 border-slate-200"
                          } border`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* INFO SIDEBAR */}
          <div className="space-y-6 animate-in slide-in-from-bottom duration-1000">

            {/* EJEMPLO PRÁCTICO */}
            <div className={`${isDarkMode ? "bg-indigo-500/10 border-indigo-500/20 shadow-lg shadow-indigo-900/10" : "bg-indigo-50 border-indigo-100"} rounded-3xl p-6 border`}>
              <div className="flex items-center gap-3 mb-5">
                <Info className="w-5 h-5 text-indigo-500" />
                <h3 className="text-[11px] font-black uppercase tracking-widest text-indigo-400">Ejemplo Práctico</h3>
              </div>
              <div className="space-y-6">
                <div className="space-y-2">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Semana Analizada</span>
                  <p className="text-sm font-black tracking-tight leading-none">{ejemplo.semana}</p>
                </div>
                <div className="h-px bg-indigo-500/10" />
                <div className="space-y-2">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Límite para pedir</span>
                  <p className={`text-xs font-bold ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{ejemplo.pedido}</p>
                </div>
                <div className="space-y-2">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Límite para modificar</span>
                  <p className={`text-xs font-bold ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{ejemplo.modificacion}</p>
                </div>
              </div>
            </div>

            {/* SECURITY / HELP */}
            <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"} rounded-3xl p-6 border shadow-sm`}>
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                <span className="text-[10px] font-black uppercase tracking-widest">Políticas Globales</span>
              </div>
              <p className={`text-[10px] font-bold leading-relaxed ${isDarkMode ? "text-slate-500" : "text-slate-500"}`}>
                Estos plazos se aplican de forma global a todos los usuarios del sistema. Los cierres se ejecutan automáticamente por el servidor según esta configuración.
              </p>
              <div className="mt-4 flex items-center gap-2 text-[10px] font-black text-indigo-400 cursor-pointer group">
                Manual de Administración <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ConfiguracionSemanal;
